import { useQuery } from '@tanstack/react-query';
import { usePost } from '../../../API';

type UseReportByIdResult = {
  /** Raw report data from either distro or DB */
  rawData: any | null;
  /** Whether the report was fetched from the DB (not distro) */
  isFromDb: boolean;
  /** Whether the query is loading */
  isLoading: boolean;
  /** Whether the query errored */
  isError: boolean;
  /** Any extra data returned alongside the result (e.g. versionId from distro) */
  extra: Record<string, any>;
  /** Re-fetch the report config from the server */
  refetch: () => Promise<any>;
}

/**
 * Fetches a report by ID, trying distro first then falling back to the DB.
 * Returns raw data — consumers are responsible for transforming it.
 */
export function useReportById(reportId: string | undefined, options?: { enabled?: boolean; staleTime?: number }): UseReportByIdResult {
  const getReportPost = usePost('/distro/report/get', { suppressErrorDisplay: true });
  const fetchReportByIdPost = usePost('/api/admin_panel/report', { suppressErrorDisplay: true });

  const { data: queryData, isLoading, isError, refetch } = useQuery({
    queryKey: ['report', reportId],
    queryFn: async () => {
      // Try distro first
      try {
        const response = await getReportPost({ id: reportId!, includeAncestry: true });
        if (response?.result?.id) {
          return response;
        }
      } catch (e) {
        // Distro failed, try DB fallback
      }
      // Fall back to DB
      const dbResponse = await fetchReportByIdPost({ reportId });
      if (dbResponse?.success && dbResponse?.data?.id) {
        return { result: { _fromDb: true, ...dbResponse.data } };
      }
      throw new Error('Report not found');
    },
    enabled: options?.enabled ?? !!reportId,
    staleTime: options?.staleTime ?? 30_000,
    retry: false,
  });

  const rawData = queryData?.result ?? null;
  const isFromDb = rawData?._fromDb === true;

  // Pass through any extra keys beyond `result` (e.g. versionId)
  const extra: Record<string, any> = {};
  if (queryData) {
    for (const [key, value] of Object.entries(queryData)) {
      if (key !== 'result') {
        extra[key] = value;
      }
    }
  }

  return { rawData, isFromDb, isLoading, isError, extra, refetch };
}
